import { c as _c } from "react/compiler-runtime";
import Button from '@precis-digital/kurama/Button';
import Typography from '@precis-digital/kurama/Typography';
import { useTheme } from '@precis-digital/kurama/utils';
import { type TooltipProps } from '@precis-digital/kurama/Tooltip';
import ThemedImage from 'shared/components/Themed/Image';
import lampInContainer from 'public/assets/images/lampInContainer.png';
import magicWandInContainer from 'public/assets/images/magicWandInContainer.png';
import settingsInContainer from 'public/assets/images/settingsInContainer.png';
import successInContainer from 'public/assets/images/successInContainer.png';
import toolsInContainer from 'public/assets/images/toolsInContainer.png';
import React from 'react';
import { StyledButtonGroup, StyledContainer, StyledDescription, StyledImageWrapper, StyledTextGroup } from './styles';
const SIZE_CONFIG = {
  small: {
    iconHeight: 64,
    iconWidth: 232,
    titleVariant: 'h5' as const,
    descVariant: 'body3' as const,
    textMaxWidth: 272
  },
  big: {
    iconHeight: 160,
    iconWidth: 580,
    titleVariant: 'h2' as const,
    descVariant: 'body1' as const,
    textMaxWidth: 364
  }
};
const VARIANT_IMAGE_MAP = {
  actionInsideAlvie: magicWandInContainer,
  actionOutsideAlvie: lampInContainer,
  noMatchingResults: settingsInContainer,
  success: successInContainer,
  error: toolsInContainer
} as const;
export type EmptyStateVariant = keyof typeof VARIANT_IMAGE_MAP;
export interface EmptyStateProps {
  size?: 'small' | 'big';
  variant?: EmptyStateVariant;
  imageAlt?: string;
  title: string;
  description?: string;
  primaryButtonLabel?: string;
  secondaryButtonLabel?: string;
  primaryButtonRightIcon?: React.ReactNode;
  secondaryButtonRightIcon?: React.ReactNode;
  primaryButtonCallback?: () => void;
  secondaryButtonCallback?: () => void;
  primaryButtonDisabled?: boolean;
  secondaryButtonDisabled?: boolean;
  primaryButtonTooltipProps?: TooltipProps;
  secondaryButtonTooltipProps?: TooltipProps;
}
const States = t0 => {
  const $ = _c(45);
  const {
    size: t1,
    variant: t2,
    imageAlt: t3,
    title,
    description: t4,
    primaryButtonLabel,
    secondaryButtonLabel,
    primaryButtonRightIcon,
    secondaryButtonRightIcon,
    primaryButtonCallback,
    secondaryButtonCallback,
    primaryButtonDisabled: t5,
    secondaryButtonDisabled: t6,
    primaryButtonTooltipProps,
    secondaryButtonTooltipProps
  } = t0;
  const size = t1 === undefined ? "big" : t1;
  const variant = t2 === undefined ? "noMatchingResults" : t2;
  const imageAlt = t3 === undefined ? "" : t3;
  const description = t4 === undefined ? "" : t4;
  const primaryButtonDisabled = t5 === undefined ? false : t5;
  const secondaryButtonDisabled = t6 === undefined ? false : t6;
  const theme = useTheme();
  const config = SIZE_CONFIG[size];
  const imageSrc = VARIANT_IMAGE_MAP[variant];
  let t7;
  if ($[0] !== theme) {
    t7 = theme.spacing(5);
    $[0] = theme;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  let t8;
  if ($[2] !== theme) {
    t8 = theme.spacing(3);
    $[2] = theme;
    $[3] = t8;
  } else {
    t8 = $[3];
  }
  let t9;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      objectFit: "contain",
      width: "100%",
      height: "100%"
    };
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  let t10;
  if ($[5] !== config.iconHeight || $[6] !== config.iconWidth || $[7] !== imageAlt || $[8] !== imageSrc) {
    t10 = <ThemedImage src={imageSrc} alt={imageAlt} width={config.iconWidth} height={config.iconHeight} style={t9} />;
    $[5] = config.iconHeight;
    $[6] = config.iconWidth;
    $[7] = imageAlt;
    $[8] = imageSrc;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== config.iconHeight || $[11] !== config.iconWidth || $[12] !== t10) {
    t11 = <StyledImageWrapper width={config.iconWidth} height={config.iconHeight}>{t10}</StyledImageWrapper>;
    $[10] = config.iconHeight;
    $[11] = config.iconWidth;
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== config.titleVariant || $[15] !== title) {
    t12 = <Typography variant={config.titleVariant}>{title}</Typography>;
    $[14] = config.titleVariant;
    $[15] = title;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== config.descVariant || $[18] !== config.textMaxWidth || $[19] !== description) {
    t13 = description !== null && description !== undefined && description !== "" && <StyledDescription $textMaxWidth={config.textMaxWidth} variant={config.descVariant}>{description}</StyledDescription>;
    $[17] = config.descVariant;
    $[18] = config.textMaxWidth;
    $[19] = description;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t12 || $[22] !== t13) {
    t14 = <StyledTextGroup>{t12}{t13}</StyledTextGroup>;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== primaryButtonCallback || $[25] !== primaryButtonDisabled || $[26] !== primaryButtonLabel || $[27] !== primaryButtonRightIcon || $[28] !== primaryButtonTooltipProps) {
    t15 = primaryButtonCallback != null && <Button variant="filled" onClick={primaryButtonCallback} rightIcon={primaryButtonRightIcon} disabled={primaryButtonDisabled} tooltipProps={primaryButtonTooltipProps}>{primaryButtonLabel}</Button>;
    $[24] = primaryButtonCallback;
    $[25] = primaryButtonDisabled;
    $[26] = primaryButtonLabel;
    $[27] = primaryButtonRightIcon;
    $[28] = primaryButtonTooltipProps;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== secondaryButtonCallback || $[31] !== secondaryButtonDisabled || $[32] !== secondaryButtonLabel || $[33] !== secondaryButtonRightIcon || $[34] !== secondaryButtonTooltipProps) {
    t16 = secondaryButtonCallback != null && <Button variant="tonal" onClick={secondaryButtonCallback} rightIcon={secondaryButtonRightIcon} disabled={secondaryButtonDisabled} tooltipProps={secondaryButtonTooltipProps}>{secondaryButtonLabel}</Button>;
    $[30] = secondaryButtonCallback;
    $[31] = secondaryButtonDisabled;
    $[32] = secondaryButtonLabel;
    $[33] = secondaryButtonRightIcon;
    $[34] = secondaryButtonTooltipProps;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== t15 || $[37] !== t16) {
    t17 = <StyledButtonGroup>{t15}{t16}</StyledButtonGroup>;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t11 || $[40] !== t14 || $[41] !== t17 || $[42] !== t7 || $[43] !== t8) {
    t18 = <StyledContainer $gap={t7} $padding={t8}>{t11}{t14}{t17}</StyledContainer>;
    $[39] = t11;
    $[40] = t14;
    $[41] = t17;
    $[42] = t7;
    $[43] = t8;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  return t18;
};
export default States;